import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Building2, Plus, Search, Loader2, Eye, Users, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const TenantsPage = () => {
  const { api } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    tin: '',
    address: '',
    phone: '',
    email: '',
    vat_mode: 'non_vat',
  });

  const loadData = async () => {
    try {
      const response = await api.get('/platform/tenants');
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [api]);

  const filteredTenants = tenants.filter(t => {
    if (!searchQuery) return true;
    return t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           t.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (t.email && t.email.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.slug) {
      toast.error('Name and slug are required');
      return;
    }

    setCreating(true);
    try {
      await api.post('/platform/tenants', newTenant);
      toast.success('Tenant created successfully');
      setShowCreateDialog(false);
      setNewTenant({
        name: '',
        slug: '',
        tin: '',
        address: '',
        phone: '',
        email: '',
        vat_mode: 'non_vat',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tenant Management</h1>
          <p className="text-muted-foreground">Manage organizations and their settings</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 w-[250px]"
              data-testid="tenant-search"
            />
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-tenant-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Tenant
          </Button>
        </div>
      </div>

      {/* Tenants List */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-250px)]">
            {filteredTenants.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No tenants found</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredTenants.map(tenant => (
                  <Link
                    key={tenant.id}
                    to={`/platform/tenants/${tenant.id}`}
                    className="block p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`tenant-row-${tenant.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-primary" />
                          <span className="font-semibold text-lg">{tenant.name}</span>
                          <Badge variant={tenant.is_active ? 'default' : 'destructive'}>
                            {tenant.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline">
                            {tenant.vat_mode === 'vat_registered' ? 'VAT Registered' : 'Non-VAT'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <span className="font-mono">{tenant.slug}</span>
                          {tenant.email && <span> | {tenant.email}</span>}
                          {tenant.phone && <span> | {tenant.phone}</span>}
                        </div>
                        {tenant.address && (
                          <div className="text-sm text-muted-foreground">{tenant.address}</div>
                        )}
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {tenant.metrics?.active_users || 0} users
                          </span>
                          <span className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4" />
                            {tenant.metrics?.sales_count || 0} sales
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Business Name *</Label>
                <Input
                  value={newTenant.name}
                  onChange={(e) => {
                    setNewTenant({ 
                      ...newTenant, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    });
                  }}
                  placeholder="e.g., Juan's Sari-Sari Store"
                  className="mt-1"
                  data-testid="tenant-name"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Slug *</Label>
                <Input
                  value={newTenant.slug}
                  onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
                  placeholder="e.g., juans-store"
                  className="mt-1 font-mono"
                  data-testid="tenant-slug"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unique identifier for this tenant (lowercase, no spaces)
                </p>
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newTenant.email}
                  onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                  placeholder="store@email.com"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Phone</Label>
                <Input
                  value={newTenant.phone}
                  onChange={(e) => setNewTenant({ ...newTenant, phone: e.target.value })}
                  placeholder="+63 912 345 6789"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>TIN</Label>
                <Input
                  value={newTenant.tin}
                  onChange={(e) => setNewTenant({ ...newTenant, tin: e.target.value })}
                  placeholder="123-456-789-000"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>VAT Mode</Label>
                <Select 
                  value={newTenant.vat_mode} 
                  onValueChange={(value) => setNewTenant({ ...newTenant, vat_mode: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_vat">Non-VAT</SelectItem>
                    <SelectItem value="vat_registered">VAT Registered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2">
                <Label>Address</Label>
                <Input
                  value={newTenant.address}
                  onChange={(e) => setNewTenant({ ...newTenant, address: e.target.value })}
                  placeholder="Business address"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTenant} disabled={creating} data-testid="confirm-create-tenant">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsPage;
